<template>
  <template v-for="role in roles" :key="role">
    <template v-if="user.members.some((item) => item.party === role)">
      <h3 class="text--uppercase fs--xs d--flex justify-content--between align-items--center">
        <span>{{ $t(`participants.subtitle.${role}`) }}</span>
        <span v-if="user.isAssistant && rolePins(role).length" class="text--normal" style="text-transform: none;">
          <template v-for="(member, index) in rolePins(role)" :key="member.id">
            <template v-if="index">, </template>{{ $t('participants.pin') }} {{ member.pin }}
            <span style="padding-right: 5px;">
              <popper :content="$t('toast.info.pin')" placement="top" hover arrow offsetDistance="8">
                <a-icon name="info" class="ms--1 text--primary"/>
              </popper>
            </span>
          </template>
        </span>
      </h3>
      <ul class="list">
        <li
          v-for="member in user.members.filter((item) => item.party === role)"
          :class="[
                    member.status === 'online' && 'is--active',
                    'py--1'
                  ]"
          :key="member.id"
        >
          <div class="avatar me--2">
            {{ member.fullname?.charAt(0) ?? member.fullName?.charAt(0) }}
          </div>
          <div class="name me--4">
            <template v-if="user.isAssistant && (member.fullName === 'Guest' || member.fullname === 'Guest') && member.contact">
              {{ member.contact }}
            </template>
            <template v-else>
              {{ member.fullName ?? member.fullname }}
            </template>
          </div>
          <div v-if="user.id !== member.id" class="action ms--auto">
            <template v-if="member.status === 'online'">
              <a-button
                v-if="user.can('exchangeRole') && member.party === 'observer'"
                variant="link-primary"
                class="px--1"
                @click="roleExchangeProcedure.offerRole(member)"
              >
                <a-icon name="swap" />
              </a-button>
              <a-button
                v-if="member.profile?.camera"
                :variant="`link-${member.profile.camera === 'not-available' ? 'danger' : 'primary'}`"
                class="px--1"
                disabled
              >
                <a-icon :name="`camera${member.profile.camera === 'on' ? '' : '-off'}`" />
              </a-button>
              <a-button
                v-if="member.profile?.microphone"
                :variant="`link-${member.profile.microphone === 'not-available' ? 'danger' : 'primary'}`"
                class="px--1"
                :disabled="!user.can('muteParticipants')"
                @click="muteHandler(member.id)"
              >
                <a-icon :name="`microphone${member.profile.microphone === 'on' ? '' : '-off'}`" />
              </a-button>
              <popper
                v-if="user.can('showInfo') && member.party === 'client'"
                v-bind="$attrs"
                :show="info"
                arrow
                placement="left"
                offsetDistance="8"
              >
                <a-button
                  variant="link-primary"
                  class="px--1"
                  @click="info = !info"
                >
                  <a-icon name="info" />
                </a-button>
                <template #content>
                  <div class="d--flex justify-content--between align-items--center mb--1">
                    <a-icon
                      name="circle-full"
                      :width="12"
                      :height="12"
                      :class="`text--${member.profile?.presence ? 'success' : 'warning'}`"
                    />
                    <a-signal v-if="!isNaN(member.profile?.signal)" :level="member.profile.signal" />
                  </div>
                  <div class="fs--sm mb--2">
                    <div>
                      {{ member.profile?.browser?.name }} {{ member.profile?.browser?.major }}
                    </div>
                    <div>
                      {{ member.profile?.os?.name }} {{ member.profile?.os?.version }}
                    </div>
                    <div>
                      <span v-if="member.profile?.device?.vendor" class="me--1">{{ member.profile.device.vendor }}</span><span v-if="member.profile?.device?.model">{{ member.profile.device.model }}</span>
                    </div>
                    <div>
                      {{ member.profile?.viewport?.width }} x {{ member.profile?.viewport?.height }}
                    </div>
                  </div>
                </template>
              </popper>
            </template>
            <a-button
              v-else-if="notificationProcedure.hasNotificationMethod(member.id)"
              variant="link-primary"
              @click="notificationProcedure.notify(member.id)"
            >
              <div v-if="notificationProcedure.state.value[member.id] === 'pending'" class="a-loader__icon a-loader__icon--md">
                <div class="a-loader__icon__content"></div>
              </div>
              <a-icon v-else-if="notificationProcedure.state.value[member.id] === 'success'" class="text--success" name="check-circle" />
              <a-icon v-else-if="notificationProcedure.state.value[member.id] === 'error'" class="text--danger" name="exclamation-mark-circle" />
              <a-icon v-else name="bell" />
            </a-button>
          </div>
        </li>
      </ul>
    </template>
  </template>
</template>

<script setup>
import {procedures} from '@communication'
import {useUserStore} from '@/stores'
import {ref} from 'vue'
import {useBroadcast} from '@/composables'
import { participant } from '@/messages'
const user = useUserStore()
const roles = ['client', 'assistant', 'observer']
const roleExchangeProcedure = procedures.get('roleExchangeProcedure')
const notificationProcedure = procedures.get('notificationProcedure')
const info = ref(false)
const broadcast = useBroadcast()

function rolePins(role) {
  return user.members.filter(member => member.party === role && member.pin)
}

function muteHandler(id) {
  if (user.can('muteParticipants')) {
    if (Number(user.id) === Number(id)) {
      user.profile.microphone === 'muted' ? user.unmute('audio') : user.mute('audio')
    } else {
      broadcast(participant.muteMessage(id, 'audio'))
    }
  }
}
</script>

<style lang="scss">
  $participants-opacity: 0.4;
  $participants-avatar-bg: $purple-200;
  $participants-avatar-color: $primary;
  $participants-name-max-width: 10rem;
  .list {
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      white-space: nowrap;
    }
  }

  .avatar {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 2rem;
    height: 2rem;
    background-color: $participants-avatar-bg;
    color: $participants-avatar-color;
    text-transform: uppercase;
    border-radius: 50%;
  }

  .name {
    max-width: $participants-name-max-width;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .is--active & {
      opacity: 1;
    }
  }

  .avatar,
  .name {
    transition-property: opacity;
    transition-duration: $duration-fast;
    opacity: $participants-opacity;

    .is--active > & {
      opacity: 1;
    }
  }

  .action {
    display: flex;
    justify-content: flex-end;
  }
</style>
